import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import * as icons from '@fortawesome/free-solid-svg-icons';
import { ShkDate } from 'shk-date';
import { ExcelService } from 'src/app/services/excel/excel.service';
import { LabelsService } from 'src/app/util/services/labels/labels.service';
import { MspTableService } from './service/msp-table.service';
import { ProfileService } from 'src/app/services/profile/profile.service';
import { DateService } from 'src/app/services/date/date.service';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { SquarePostLoadingEffectComponent } from '../square-post-loading-effect/square-post-loading-effect.component';
import { PaginationCounterModule } from '../pagination-counter/pagination-counter.module';
import { MspPipesModule } from 'src/app/util/pipes/msp-pipes.module';
import { LabelsInterface } from 'src/app/util/services/labels/data/label.inteface';
import { TooltipDirective } from 'src/app/util/directives/tooltip/tooltip.directive';
import { genericFormI } from 'src/app/util/interfaces/genericFormRMK';

export interface mspTableConfigInterface {
	tableViewType: 'table' | 'box';
	itemsPerPage: 10 | 20 | 50;
}

export interface mspTableHeaderI {
	name: string;
	value: string;
	colType: 'date' | 'time' | 'boolean' | 'Checkbox' | 'multi-input' | 'multi-select' | 'text' | 'icon' | 'template' | 'action' | 'none';
	colWidth?: string;
	template?: any;
	items?: genericFormI['items'][]; // Para colunas com busca do tipo select
	startSearch?: genericFormI['values']; // Para colunas com busca do tipo texto
	ellipsis?: boolean;
	nowrap?: boolean;

	actions?: {
		icon: icons.IconDefinition;
		onClick: (line: any) => void;
		tooltip?: string;
	}[];
}

@Component({
	standalone: true,
	imports: [
		CommonModule,
		RouterModule,
		FontAwesomeModule,
		SquarePostLoadingEffectComponent,
		PaginationCounterModule,
		MspPipesModule,
		TooltipDirective,
	],
	selector: 'cpi-msp-table',
	templateUrl: './msp-table.component.html',
	styleUrls: ['./msp-table.component.scss'],
})
export class MspTableComponent implements OnInit, OnChanges {
	_icons = {};

	@Input()
	labels: LabelsInterface = {
		lbl_geral_sim: '',
		lbl_geral_nao: '',
		lbl_chat_analyst_configuracoes: '',
		lbl_tipo_visualizacao: '',
		lbl_geral_tabela: '',
		lbl_geral_caixas_2: '',
		lbl_geral_exportar_dados: '',
		lbl_exportar_esta_pagina_excel: '',
		lbl_geral_resultados_por_pagina: '',
		lbl_configuracoes_reinicio_pagina: '',
		lbl_itens_da_listagem: '',
		lbl_tabela_sem_customizacao_disponivel: '',
		lbl_alteracoes_aplicadas_apenas_para_esta_tabela: '',
		lbl_geral_pesquisar: '',
	};

	@ViewChild('horizontalScrollContainer') horizontalScrollContainer: ElementRef;

	_saveTimeOut: NodeJS.Timeout = undefined;
	_saveConfigTimeOut: NodeJS.Timeout = undefined;

	@Input()
	isLoading = false;

	@Input()
	height: string;
	@Input()
	maxHeight: string;
	@Input()
	itemPadding = '1rem';
	@Input()
	tableOnly = false;

	//Header é composto por name e value, nome é a chave dos objetos, como, id, name, identification, summary, que
	//vão ser utilizados no sort, se o nome dado não corresponder a uma chave dos itens no body a celula aparecerá vazia.
	//O valor representa a label do header.
	customHeaders = false;
	@Input()
	header: mspTableHeaderI[] = [];
	@Input()
	availableHeadersOptions: mspTableHeaderI[] = [];
	_availableHeadersOptionsDictionary = {};
	_headerKeys = [];
	_headerFilter = '';

	//O body espera um array de objeto qualquer, chave e valor, o match e sort é correspondente com a chave
	//que foi colocada no header, não é esperado um array 2D.
	@Input()
	body: any[] = [];

	@Input()
	searchable = false;
	searchText = '';
	_displayBody: any[] = [];

	//Este valor é verdadeiro se for para o body ser ordenado internamente. Quando a tabela está
	//paginada, geralmente a ordenação não é feita internamente, dado que os itens no body
	//estão filtrados e com a visão limitada.
	@Input()
	insideSort = false;
	@Input()
	insidePagination = true;
	//InsidePaginationVars
	@Input()
	selectedPage = 1;
	_paginationStart = 0;
	_paginationEnd = 0;
	@Output()
	getSelectedPage = new EventEmitter();

	_sortBy: string;
	_sortByIndex = 0;
	@Input()
	sortDown = false;
	_sortDown = false;

	//Router link, permite uso de variável como nas rotas do angular :id,
	//será substituido se a chave id no existor no body
	@Input()
	itemRouterLink = '';
	@Input()
	markLastSelectedItem = false;
	@Output()
	lastSelectedItem = new EventEmitter();
	_lastSelectedItem = undefined;

	@Output()
	getSort = new EventEmitter<{ sortBy: string; sortDown: boolean }>();
	@Output()
	getSelectedItem = new EventEmitter();
	@Output()
	getSelectedItemIndex = new EventEmitter();

	_isTableOptionOpen = false;
	_mspTableConfig: mspTableConfigInterface = {
		tableViewType: 'table',
		itemsPerPage: 10,
	};

	constructor(
		private LabelService: LabelsService,
		private ExcelService: ExcelService,
		private mspTableService: MspTableService,
		public ProfileService: ProfileService,
		public DateService: DateService
	) {
		this.filterHeader = this.filterHeader.bind(this);
		this.LabelService.getLabels().subscribe((labels) => {
			this.labels = labels;
		});
	}

	ngOnInit(): void {
		this._sortBy = this.header.find((e) => {
			return e.colType != 'icon' && e.name != undefined;
		}).name;
		this._sortDown = this.sortDown;
		this._headerKeys = this.header.map((headerI) => {
			return headerI.name;
		});
		this._icons = icons;
		if (this.availableHeadersOptions.length === 0) this.availableHeadersOptions = [...this.header];
		else this.customHeaders = true;

		for (const options of this.availableHeadersOptions) {
			this._availableHeadersOptionsDictionary[options.name] = options;
		}

		this._mspTableConfig = this.mspTableService.getTableConfig();
		this._paginationEnd = this.mspTableService.getTableConfig('itemsPerPage');
		if (this.tableOnly) {
			this._mspTableConfig.tableViewType = 'table';
			this._mspTableConfig.itemsPerPage = 20;
			this._paginationEnd = 20;
		}
		this.sort();
		this.updateDisplayedBody();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes && changes.header)
			this._headerKeys = this.header.map((headerI) => {
				return headerI.name;
			});
		if (changes && changes.sortDown) this._sortDown = this.sortDown;
		if (changes && changes.body) {
			this.selectedPage = 1;
			this._paginationStart = 0;
			this._paginationEnd = this.mspTableService.getTableConfig('itemsPerPage');
		}
		this.updateDisplayedBody();
	}

	changeSort(sortBy: string): void {
		if (this._sortBy == sortBy) {
			this._sortDown = !this._sortDown;
			this.getSort.emit({ sortBy: this._sortBy, sortDown: this._sortDown });
		} else {
			this._sortBy = sortBy;
			this._sortDown = true;
			this.getSort.emit({ sortBy: this._sortBy, sortDown: this._sortDown });
		}
		this.selectedPage = 1;
		this._paginationStart = 0;
		this._paginationEnd = this._mspTableConfig.itemsPerPage;
		this.emitSelectedPage();
		if (this.insideSort) this.sort();
		this.updateDisplayedBody();
	}

	sort(): void {
		this.body?.sort((a, b) => {
			if (a[this._sortBy] > b[this._sortBy]) return this._sortDown ? 1 : -1;
			if (a[this._sortBy] < b[this._sortBy]) return this._sortDown ? -1 : 1;
			return 0;
		});
	}

	onSearchChange(searchValue: string): void {
		this.searchText = searchValue;
		this.selectedPage = 1;
		this._paginationStart = 0;
		this._paginationEnd = this._mspTableConfig.itemsPerPage;
		this.emitSelectedPage();
		this.updateDisplayedBody();
	}

	updateDisplayedBody(): void {
		if (!this.searchable || this.searchText.trim() == '') {
			this._displayBody = [...(this.body || [])];
			return;
		}

		const normalizedSearch = this.searchText.toLowerCase().trim();
		const searchableHeaders = this.header.filter((header) => {
			return !['icon', 'template', 'action', 'none'].includes(header.colType) && !!header.name;
		});

		this._displayBody = (this.body || []).filter((tableLine) => {
			return searchableHeaders.some((header) => {
				return this.getSearchValue(tableLine, header).includes(normalizedSearch);
			});
		});
	}

	getSearchValue(tableLine: any, header: mspTableHeaderI): string {
		const cellValue = tableLine?.[header.name];

		if (cellValue === undefined || cellValue === null) return '';
		if (header.colType === 'boolean') return (this.formatBoolean(cellValue) || '').toLowerCase();
		if (header.colType === 'date') return (this.DateService.formatDate(cellValue) || '').toLowerCase();
		if (header.colType === 'time') return (this.formatTime(cellValue) || '').toLowerCase();
		if (['multi-input', 'multi-select', 'Checkbox'].includes(header.colType)) {
			return this.formatSpecialCharacter(cellValue.toString()).toLowerCase();
		}

		return cellValue.toString().toLowerCase();
	}

	formatSpecialCharacter(string_: string) {
		let rString = string_.replaceAll('§', ', ');
		rString = rString.slice(0, -2);
		return rString;
	}

	formatBoolean(boolString: string): string {
		if (boolString === undefined || boolString === null || boolString === '') return '';
		if (boolString.toString() == 'true' || boolString.toString() == '1') return this.labels.lbl_geral_sim;
		if (boolString.toString() == 'false' || boolString.toString() == '0') return this.labels.lbl_geral_nao;
		return boolString.toString();
	}

	formatTime(timeString: string): string {
		if (!timeString || timeString == '') return 'N/A';

		const sTimeString = timeString.split(':');

		if (sTimeString.length == 3) {
			return `${sTimeString[0]}h ${sTimeString[1]}m ${sTimeString[2]}s`;
		}
		if (sTimeString.length == 2) {
			return `${sTimeString[0]}h ${sTimeString[1]}m`;
		}
		if (sTimeString.length == 1) {
			return `${sTimeString[0]}h`;
		}

		return 'N/A';
	}

	getItem(index: number): void {
		const itemPageIndex = this._mspTableConfig.itemsPerPage * this.selectedPage - (this._mspTableConfig.itemsPerPage - index);
		this.getSelectedItem.emit([this._displayBody[itemPageIndex]]);
		this.getSelectedItemIndex.emit(itemPageIndex);
	}

	goTo(index: number): string {
		const itemPageIndex = this._mspTableConfig.itemsPerPage * this.selectedPage - (this._mspTableConfig.itemsPerPage - index);
		const selectedItem = this._displayBody[itemPageIndex];
		const parameters = this.itemRouterLink.split(/[/:]/);
		let itemRouterLink = this.itemRouterLink;
		for (const parameter of parameters) {
			if (parameter != '') itemRouterLink = itemRouterLink.replace(`:${parameter}`, selectedItem[parameter]);
		}
		return itemRouterLink;
	}

	horizontalScroll(e: MouseEvent | WheelEvent, side: 'left' | 'right'): void {
		let scrollValue = 0;
		if (e.type == 'wheel' && (<WheelEvent>e).deltaY > 0)
			scrollValue = this.horizontalScrollContainer.nativeElement.scrollWidth * 0.12 * -1;
		if (e.type == 'wheel' && (<WheelEvent>e).deltaY < 0) scrollValue = this.horizontalScrollContainer.nativeElement.scrollWidth * 0.12;
		if (e.type == 'click' && side == 'left') scrollValue = this.horizontalScrollContainer.nativeElement.scrollWidth * 0.12 * -1;
		if (e.type == 'click' && side == 'right') scrollValue = this.horizontalScrollContainer.nativeElement.scrollWidth * 0.12;

		(<HTMLElement>this.horizontalScrollContainer.nativeElement).scrollBy({
			left: scrollValue,
			behavior: 'smooth',
		});
	}

	setHeader(itemName: string): void {
		const item = this._availableHeadersOptionsDictionary[itemName];
		if (this.header.includes(item)) this.header.splice(this.header.indexOf(item), 1);
		else this.header.push(item);
		this._headerKeys = this.header.map((headerI) => {
			return headerI.name;
		});
		clearTimeout(this._saveTimeOut);
		this._saveTimeOut = setTimeout(() => {
			this.mspTableService.setTableColumns('request', this.header).subscribe();
		}, 2000);
	}

	filterHeader(header: mspTableHeaderI): boolean {
		let headerValue = header.value;
		if (this.labels[header.value] != undefined) headerValue = this.labels[header.value];
		return this._headerFilter.trim() == '' || headerValue.toLowerCase().trim().includes(this._headerFilter.toLowerCase().trim());
	}

	setTableViewType(viewType: string): void {
		this.mspTableService.setTableConfig('tableViewType', viewType);
		clearTimeout(this._saveTimeOut);
		this._saveTimeOut = setTimeout(() => {
			this.mspTableService.saveTableConfig().subscribe();
		}, 2000);
	}

	setTableItemsPerPage(itemsPerPage: number): void {
		this.mspTableService.setTableConfig('itemsPerPage', itemsPerPage);
		this._saveTimeOut = setTimeout(() => {
			this.mspTableService.saveTableConfig().subscribe();
		}, 2000);
	}

	exportTable(): void {
		const exportArray = [];
		let temporary = [];
		for (const header of this.header) {
			temporary.push(header.value);
		}
		exportArray.push(JSON.parse(JSON.stringify(temporary)));
		for (const line of this._displayBody) {
			temporary = [];
			for (const headerKey of this._headerKeys) {
				temporary.push(line[headerKey]);
			}
			exportArray.push(JSON.parse(JSON.stringify(temporary)));
		}
		this.ExcelService.exportAsExcelFile(exportArray, 'mspFile');
	}

	emitSelectedPage(): void {
		this.getSelectedPage.emit(this.selectedPage);
	}

	formatIconName(cellName: any): string {
		return cellName.trim().split(/\s+/)[0];
	}

	formatIconTooltip(cellName: string): string {
		if (!cellName) return '';

		const withoutIcon = cellName.replace(/^\S+\s*/, '');
		const splitted = withoutIcon.split(' ');
		const labelKey = splitted.shift() || '';
		const complement = splitted.join(' ');
		const label = this.labels[labelKey] || labelKey;

		return `${label}${complement ? ' ' + complement : ''}`;
	}
}
